<template>
  <el-scrollbar class="scroll-bar">
    <div
      class="min-h-screen bg-[#0f1729] text-white p-6 font-sans"
      style="background-color: #0f1729"
      v-loading="loadingEvent"
      element-loading-background="rgba(24, 33, 48, 0.8)"
    >
      <div class="max-w-2xl mx-auto">
        <div class="flex items-start justify-between mb-4">
          <div>
            <h1 class="text-3xl font-bold mb-3">警報詳情</h1>
            <p class="text-slate-400 text-base leading-relaxed">
              {{ pageSubtitle }}
            </p>
          </div>
          <button
            class="text-slate-400 hover:text-white transition-colors"
            aria-label="關閉"
            type="button"
            @click="handleCloseWindow"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="32"
              height="32"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="lucide lucide-x"
            >
              <path d="M18 6 6 18"></path>
              <path d="m6 6 12 12"></path>
            </svg>
          </button>
        </div>

        <div
          v-if="errorMessage"
          class="mt-6 rounded-lg border border-red-500/40 bg-red-500/10 p-4 text-sm text-red-200"
        >
          {{ errorMessage }}
        </div>

        <div v-else>
          <div v-if="eventDetail" class="mt-12 mb-8">
            <h2 class="text-3xl font-bold mb-6">{{ eventTitle }}</h2>
            <p
              v-if="eventDetail.event"
              class="text-slate-300 leading-relaxed mb-6"
            >
              {{ eventDetail.event }}
            </p>
            <div class="space-y-5">
              <div class="flex items-center justify-between">
                <span class="text-slate-400 text-lg">設備：</span>
                <span class="text-white text-lg font-medium">
                  {{ eventDeviceLabel }}
                </span>
              </div>
              <div class="flex items-center justify-between">
                <span class="text-slate-400 text-lg">級別：</span>
                <span class="flex-shrink-0">
                  <RenderEventLogLevel :model-value="eventLevelValue" size="xlarge"/>
                </span>
              </div>
              <div class="flex items-center justify-between">
                <span class="text-slate-400 text-lg">狀態：</span>
                <span class="alert-status">{{ alertStatusLabel }}</span>
              </div>
              <div class="flex items-center justify-between">
                <span class="text-slate-400 text-lg">觸發時間：</span>
                <span class="text-white text-lg font-medium">
                  {{ triggeredAtLabel }}
                </span>
              </div>
              <div class="flex items-center justify-between">
                <span class="text-slate-400 text-lg">升級級別：</span>
                <span class="text-white text-lg font-medium">
                  {{ escalationLabel }}
                </span>
              </div>
            </div>

            <div
              v-if="showResumedBanner"
              class="mt-6 rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-4 text-sm text-emerald-100"
            >
              此警報已從暫停模式恢復，現正重新監察。
            </div>
          </div>

          <div
            v-else
            class="mt-12 rounded-lg border border-slate-700/60 bg-slate-800/30 p-6 text-center text-slate-400"
          >
            此連結沒有可用的警報資訊。
          </div>

          <div class="border-t border-slate-700/50 my-8"></div>

          <div class="mt-12">
            <h2 class="text-3xl font-bold mb-8">提供回饋</h2>
            <div class="mb-8">
              <h3 class="text-xl font-semibold mb-5">回饋類型</h3>
              <div class="grid grid-cols-2 gap-4 mb-6">
                <button
                  type="button"
                  class="flex items-center justify-center gap-3 py-4 px-6 rounded-lg border-2 transition-all bg-slate-800/30"
                  :class="
                    feedbackType === 'True'
                      ? 'border-green-500 text-green-500 hover:border-green-400'
                      : 'border-slate-700 text-slate-300 hover:border-slate-600'
                  "
                  @click="selectFeedback('True')"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="lucide lucide-thumbs-up"
                  >
                    <path d="M7 10v12"></path>
                    <path
                      d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2h0a3.13 3.13 0 0 1 3 3.88Z"
                    ></path>
                  </svg>
                  <span class="text-lg font-medium">正確</span>
                </button>
                <button
                  type="button"
                  class="flex items-center justify-center gap-3 py-4 px-6 rounded-lg border-2 transition-all bg-slate-800/30"
                  :class="
                    feedbackType === 'False'
                      ? 'border-red-500 text-red-500'
                      : 'border-slate-700 text-slate-300 hover:border-slate-600'
                  "
                  @click="selectFeedback('False')"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="lucide lucide-thumbs-down"
                    :class="[
                      feedbackType === 'False'
                        ? 'fill-red-500'
                        : 'fill-green-500',
                    ]"
                  >
                    <path d="M17 14V2"></path>
                    <path
                      d="M9 18.12 10 14H4.17a2 2 0 0 1-1.92-2.56l2.33-8A2 2 0 0 1 6.5 2H20a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-2.76a2 2 0 0 0-1.79 1.11L12 22h0a3.13 3.13 0 0 1-3-3.88Z"
                    ></path>
                  </svg>
                  <span class="text-lg font-medium">錯誤</span>
                </button>
              </div>
              <el-input
                v-model="feedbackText"
                type="textarea"
                :rows="4"
                placeholder="補充更多背景資料或已採取的行動"
                maxlength="500"
                show-word-limit
                class="mb-4"
              />
              <NotificationSnoozeCard
                v-if="showSnoozePanel"
                ref="notificationSnoozeCard"
                :token="token"
                @status-change="handleSnoozeStatusChange"
              />
            </div>
            <div class="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <el-button
                data-slot="button"
                class="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive border shadow-xs dark:bg-input/30 dark:border-input dark:hover:bg-input/50 h-9 w-full px-4 has-[>svg]:px-3 py-6 text-lg font-medium bg-slate-800/30 border-slate-700 text-slate-300 hover:bg-slate-800/50 hover:text-white sm:col-span-1"
                @click="handleCloseWindow"
              >
                關閉
              </el-button>
              <div class="flex items-center sm:col-span-2">
                <el-button
                  class="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md transition-all disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive h-9 px-4 has-[>svg]:px-3 py-6 text-lg font-medium bg-amber-600 hover:bg-amber-700 text-slate-900 disabled:opacity-50 disabled:cursor-not-allowed w-full"
                  size="large"
                  type="primary"
                  :loading="submittingFeedback"
                  :disabled="!canSubmit"
                  @click="handleSubmitFeedback"
                >
                  提交回饋
                </el-button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </el-scrollbar>
</template>

<script>
import {
  getPublicNotificationEvent,
  createPublicNotificationAiAudit,
  updatePublicNotificationAiAudit,
} from "@/api";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
dayjs.extend(utc);
dayjs.extend(timezone);
import { EventLogStatus, enumValueToLabel } from "@/enums";
import RenderEventLogLevel from "@/components/RenderEventLogLevel.vue";
import NotificationSnoozeCard from "./components/NotificationSnoozeCard.vue";

export default {
  name: "AlertDetailFromWhatsApp",
  components: {
    NotificationSnoozeCard,
    RenderEventLogLevel,
  },
  data() {
    return {
      token: (this.$route.query.token || "").trim(),
      loadingEvent: false,
      submittingFeedback: false,
      eventDetail: null,
      aiAuditList: [],
      feedbackType: "",
      feedbackText: "",
      activeAuditId: null,
      errorMessage: "",
      snoozeState: {
        active: false,
        supported: true,
        loaded: false,
        message: "",
        until: "",
        error: "",
      },
    };
  },
  computed: {
    isResumeRoute() {
      return this.$route.name === "notification-resume";
    },
    pageSubtitle() {
      if (
        this.snoozeState.loaded &&
        this.snoozeState.supported &&
        this.snoozeState.active
      ) {
        return (
          this.snoozeState.message ||
          "此警報目前已暫停，將會自動恢復。"
        );
      }
      if (
        this.isResumeRoute &&
        this.snoozeState.loaded &&
        this.snoozeState.supported &&
        !this.snoozeState.active
      ) {
        return "警報已在暫停時間結束後恢復";
      }
      return "此警報的完整記錄及通知紀錄";
    },
    eventTitle() {
      if (!this.eventDetail) {
        return "警報";
      }
      return this.eventDetail.name || this.eventDetail.event || "警報";
    },
    eventLevelValue() {
      if (!this.eventDetail || typeof this.eventDetail.level === "undefined") {
        return undefined;
      }
      return this.eventDetail.level;
    },
    alertStatusLabel() {
      if (
        this.snoozeState.loaded &&
        this.snoozeState.supported &&
        this.snoozeState.active
      ) {
        return "已暫停";
      }
      if (!this.eventDetail || this.eventDetail.status === undefined) {
        return "--";
      }
      const status = this.eventDetail.status;
      const label = enumValueToLabel(EventLogStatus, status);
      const statusLabelMap = {
        "pending feedback": "待回饋",
        reviewed: "已審閱",
        resolved: "已解決",
      };
      const normalizedLabel = String(label || "")
        .trim()
        .replace(/[_-]+/g, " ")
        .toLowerCase();
      const normalizedStatus = String(status || "")
        .trim()
        .replace(/[_-]+/g, " ")
        .toLowerCase();
      if (label !== "--") {
        return statusLabelMap[normalizedLabel] || label;
      }
      if (typeof status === "string" && status.length) {
        return statusLabelMap[normalizedStatus] || status;
      }
      return status;
    },
    triggeredAtLabel() {
      if (!this.eventDetail) {
        return "--";
      }
      const timestamp =
        this.eventDetail.isoTimeString ||
        this.eventDetail.timestamp ||
        this.eventDetail.createdAt ||
        this.eventDetail.sentAt;
      return this.formatTimestamp(timestamp);
    },
    escalationLabel() {
      if (!this.eventDetail || this.eventDetail.escalated === undefined) {
        return "--";
      }
      if (!this.eventDetail.escalated) {
        return "無";
      }
      return `第 ${this.eventDetail.escalated} 級`;
    },
    eventDeviceLabel() {
      if (!this.eventDetail) {
        return "--";
      }
      return this.eventDetail.assetName || this.eventDetail.deviceKey || "--";
    },
    showSnoozePanel() {
      return this.feedbackType === "False" || this.snoozeState.active;
    },
    showResumedBanner() {
      return (
        this.isResumeRoute &&
        this.snoozeState.loaded &&
        this.snoozeState.supported &&
        !this.snoozeState.active
      );
    },
    canSubmit() {
      return (
        !!this.token &&
        !!this.feedbackType &&
        (this.feedbackType === "True" || !!this.feedbackText) &&
        !this.submittingFeedback
      );
    },
  },
  watch: {
    "$route.query.token": {
      immediate: false,
      handler(nextToken) {
        this.token = (nextToken || "").trim();
        this.initialize();
      },
    },
  },
  created() {
    this.initialize();
  },
  methods: {
    async initialize() {
      this.eventDetail = null;
      this.aiAuditList = [];
      this.errorMessage = "";
      if (!this.token) {
        this.errorMessage =
          "缺少 token。請從通知訊息中開啟連結。";
        return;
      }
      await this.fetchEventDetail();
    },
    async fetchEventDetail() {
      this.loadingEvent = true;
      try {
        const data = await getPublicNotificationEvent(this.token);
        this.eventDetail = data && data.event ? data.event : null;
        const historyList = data && data.aiAuditList ? data.aiAuditList : [];
        const eventAudit =
          this.eventDetail && this.eventDetail.aiAudit
            ? this.eventDetail.aiAudit
            : null;

        if (eventAudit) {
          this.activeAuditId = eventAudit.id;
          this.feedbackType = eventAudit.type || "";
          this.feedbackText = eventAudit.feedback || "";
        } else if (historyList.length) {
          const latest = historyList[0];
          this.activeAuditId = latest.id;
          this.feedbackType = latest.type || "";
          this.feedbackText = latest.feedback || "";
        } else {
          this.activeAuditId = null;
          this.feedbackType = "";
          this.feedbackText = "";
        }

        if (
          eventAudit &&
          (!historyList.length || historyList[0].id !== eventAudit.id)
        ) {
          this.aiAuditList = [eventAudit, ...historyList];
        } else if (historyList.length) {
          this.aiAuditList = historyList;
        } else if (eventAudit) {
          this.aiAuditList = [eventAudit];
        } else {
          this.aiAuditList = [];
        }
      } catch (error) {
        this.errorMessage = this.normalizeErrorMessage(
          error,
          "無法載入警報詳情。"
        );
      } finally {
        this.loadingEvent = false;
      }
    },
    selectFeedback(type) {
      this.feedbackType = type;
    },
    formatTimestamp(value) {
      if (!value) {
        return "--";
      }
      const normalizedValue =
        typeof value === "string" ? value.replace(/\[[^\]]+\]$/, "") : value;
      const d = dayjs(normalizedValue).tz("Asia/Hong_Kong");
      if (!d.isValid()) {
        return String(value);
      }
      return d.format("YYYY-MM-DD HH:mm:ss Z");
    },
    normalizeErrorMessage(error, fallback) {
      const status = error && error.response && error.response.status;
      if (status === 400) {
        return "請求無效，請檢查連結或提交內容。";
      }
      if (status === 404) {
        return "找不到相關警報資訊，請確認連結是否正確或已過期。";
      }
      if (status) {
        return `請求失敗，狀態碼：${status}`;
      }
      return (
        (error && error.message) ||
        (error && error.msg) ||
        (error && error.data && error.data.message) ||
        fallback
      );
    },
    async handleSubmitFeedback() {
      if (!this.canSubmit) {
        this.notify("warning", "請選擇回饋類型並輸入詳情。");
        return;
      }
      this.submittingFeedback = true;
      const payload = {
        type: this.feedbackType,
        feedback: this.feedbackText.trim(),
      };
      try {
        if (this.showSnoozePanel && this.$refs.notificationSnoozeCard) {
          const snoozeResult =
            await this.$refs.notificationSnoozeCard.submitIfNeeded();
          if (snoozeResult && snoozeResult.valid === false) {
            return;
          }
        }
        if (this.activeAuditId) {
          await updatePublicNotificationAiAudit(this.token, {
            id: this.activeAuditId,
            ...payload,
          });
        } else {
          await createPublicNotificationAiAudit(this.token, payload);
        }
        this.notify("success", "回饋已成功提交。");
        await this.fetchEventDetail();
      } catch (error) {
        this.notify(
          "error",
          this.normalizeErrorMessage(error, "提交回饋失敗。")
        );
      } finally {
        this.submittingFeedback = false;
      }
    },
    handleCloseWindow() {
      window.close();
    },
    handleSnoozeStatusChange(status) {
      this.snoozeState = {
        ...this.snoozeState,
        ...status,
      };
    },
    notify(type, message) {
      if (this.$message && typeof this.$message[type] === "function") {
        this.$message[type](message);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.alert-status {
  display: inline-flex;
  align-items: center;
  padding: 0.5rem 1.25rem;
  border-radius: 9999px;
  background: rgba(251, 146, 60, 0.1);
  color: #fb923c;
  border: 1px solid rgba(251, 146, 60, 0.3);
  font-size: 1rem;
  font-weight: 500;
}
</style>
